/**
 * Stamping host-fired Heap events with the Ash session they belong to.
 *
 * Four of Ash's Heap events don't come from Ash. `ashClosed`,
 * `ashExpandToggled` and `ashMaximizeToggled` fire from the poptart chrome that
 * wraps the iframe, and `memberCareChatOpenedFromAsh` fires from this app's
 * Genesys hand-off — all of them outside the iframe, in code that has no access
 * to the session id minted inside it. The iframe posts `ASH_SESSION_STARTED`
 * when the member sends their first message; `SidekickProvider` holds the id
 * and these events read it through here.
 *
 * The events Ash itself sends need none of this — `TRACK_EVENT` already carries
 * `sessionId` in its payload and the provider forwards it verbatim.
 *
 * Mirrors `apps/member-app/lib/ash-session.ts`. Kept as two files rather than a
 * shared package because the two apps' Heap call styles differ (this one calls
 * `window.heap.track` directly, member-app goes through `lib/heap`), and both
 * are a handful of lines — see the member-app copy for the same reasoning.
 *
 * @param {string} ashSessionId - The live Ash session, or '' before the first
 *   message.
 * @param {object} [properties] - The event's own properties, if any.
 * @returns {object | undefined} The properties with `ashSessionId` added, or
 *   unchanged when there is no session yet.
 */
export const withAshSession = (ashSessionId, properties) => {
  // Absent, never empty. `ashSessionId: ''` in Heap is indistinguishable from a
  // real-but-blank id, and the state it would describe is a genuine one: the
  // member opened Ash, typed nothing, and closed it. That `ashClosed` has no
  // conversation to join to, and a blank id invents a session with no messages
  // and no persisted record. An absent property reads unambiguously as "no
  // session started" — and `ashOpenedFromHelpIcon` is already the
  // screen-opened-without-a-message signal, so nothing is lost.
  if (!ashSessionId) return properties

  return { ...properties, ashSessionId }
}
